import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addBrokerageManager,
  getManagedBrokerage,
  getManagedBrokerages,
  getMyBrokerage,
  removeBrokerageManager,
  saveBrokerageBranding,
  saveFeaturedAgents,
  saveHiddenAgents,
  saveBrokerageTeams,
} from "@/services/brokerageService";

export const MANAGED_BROKERAGES_KEY = ["managedBrokerages"];

const resultOf = (response, fallback) => {
  if (response?.errorCode !== 0) {
    const error = new Error(response?.message || fallback);
    error.fields = response?.data?.errors || null;
    throw error;
  }
  return response.result;
};

// The API answers bad input with an HTTP 4xx and the error envelope in the body,
// which axios throws as an AxiosError. Turn that back into the same Error shape
// resultOf builds so callers always see `message` and `fields`.
export const callOf = async (run, fallback) => {
  let response;
  try {
    response = await run();
  } catch (caught) {
    const body = caught?.response?.data;
    if (!body || typeof body !== "object") throw caught;
    response = body;
  }
  return resultOf(response, fallback);
};

export const useManagedBrokerages = (enabled = true) =>
  useQuery({
    queryKey: MANAGED_BROKERAGES_KEY,
    queryFn: () => callOf(() => getManagedBrokerages(), "Could not load your brokerage pages."),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

export const useMyBrokerage = (enabled = true) =>
  useQuery({
    queryKey: ["myBrokerage"],
    queryFn: () => callOf(() => getMyBrokerage(), "Could not load your brokerage page."),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

export const managedBrokerageKey = (id) => ["managedBrokerage", id];

export const useManagedBrokerage = (id) =>
  useQuery({
    queryKey: managedBrokerageKey(id),
    queryFn: () => callOf(() => getManagedBrokerage(id), "Could not load this brokerage page."),
    enabled: Boolean(id),
  });

const useBrokerageMutation = (id, run, fallback) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => callOf(() => run(id, input), fallback),
    onSuccess: (data) => {
      queryClient.setQueryData(managedBrokerageKey(id), data);
      queryClient.invalidateQueries({ queryKey: MANAGED_BROKERAGES_KEY });
      queryClient.invalidateQueries({ queryKey: ["brokerage"] });
      queryClient.invalidateQueries({ queryKey: ["agentDirectory"] });
    },
  });
};

export const useSaveBrokerageBranding = (id) =>
  useBrokerageMutation(id, saveBrokerageBranding, "Your changes did not save.");

export const useSaveFeaturedAgents = (id) =>
  useBrokerageMutation(id, saveFeaturedAgents, "Featured agents did not save.");

export const useSaveHiddenAgents = (id) =>
  useBrokerageMutation(id, saveHiddenAgents, "That change to your page did not save.");

export const useSaveBrokerageTeams = (id) =>
  useBrokerageMutation(id, saveBrokerageTeams, "Your teams did not save.");

export const useAddBrokerageManager = (id) =>
  useBrokerageMutation(id, addBrokerageManager, "That manager could not be added.");

export const useRemoveBrokerageManager = (id) =>
  useBrokerageMutation(id, removeBrokerageManager, "That manager could not be removed.");
